import React, { useEffect, useState } from 'react';
import { useAuth0, RedirectLoginOptions } from '@auth0/auth0-react';
import { Shield, AlertCircle, LogIn, Loader2 } from 'lucide-react';
import { useAdminStatus } from '../hooks/useAdminStatus';
import { useFinanceStatus } from '../hooks/useFinanceStatus';
import { useAuditLog } from '../hooks/useAuditLog';
import { use2FA } from '../hooks/use2FA';
import UserRegistrationForm from './UserRegistrationForm';
import { PendingApproval } from './PendingApproval';
import { AccessDenied } from './AccessDenied';
import FinanceDashboard from './FinanceDashboard';
import { TwoFactorVerificationModal } from './TwoFactorVerificationModal';
import { TwoFactorSetupPage } from './TwoFactorSetupPage';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isLoading, isAuthenticated, error, loginWithRedirect, user, logout } = useAuth0();
  const { adminStatus, admin, loading: adminLoading, registerAdmin } = useAdminStatus();
  const { financeStatus, financeUser, loading: financeLoading, registerFinanceUser } = useFinanceStatus();
  const { is2FAEnabled, loading: twoFALoading } = use2FA();
  const { logAction } = useAuditLog();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [hasLoggedLogin, setHasLoggedLogin] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const twoFASetupSkipKey = user?.sub ? `2fa_setup_offered:${user.sub}` : null;
  const [is2FAVerified, setIs2FAVerified] = useState(() => {
    // Check if 2FA was already verified in this session
    return sessionStorage.getItem('2fa_verified') === 'true';
  });
  const [has2FASetupBeenOffered, setHas2FASetupBeenOffered] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return Object.keys(sessionStorage).some((key) => key.startsWith('2fa_setup_offered:'));
  });

  // Determine overall user status
  const userApproved = adminStatus === 'APPROVED' || financeStatus === 'APPROVED';
  const userPending = adminStatus === 'PENDING' || financeStatus === 'PENDING';
  const userDenied = adminStatus === 'DENIED' || financeStatus === 'DENIED';
  const userNotFound = adminStatus === 'NOT_FOUND' && financeStatus === 'NOT_FOUND';
  const overallLoading = adminLoading || financeLoading || twoFALoading;

  // Allow faster local development by bypassing interactive 2FA verification on localhost only.
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const bypass2FAInDev = import.meta.env.DEV && isLocalhost;

  // Check if 2FA verification is needed
  const needs2FA = userApproved && is2FAEnabled && !is2FAVerified && !bypass2FAInDev;
  
  // Check if 2FA setup prompt is needed (approved user without 2FA enabled, and hasn't been offered yet)
  const needs2FASetup = userApproved && is2FAEnabled === false && !twoFALoading && !has2FASetupBeenOffered;

  const getCurrentReturnTo = () => {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  };

  const loginOptions: RedirectLoginOptions = {
    connection: 'google-oauth2',
    appState: {
      returnTo: getCurrentReturnTo(),
    },
  };

  // Effect to show 2FA modal when needed
  useEffect(() => {
    if (needs2FA && !show2FAModal) {
      setShow2FAModal(true);
    }
  }, [needs2FA, show2FAModal]);

  useEffect(() => {
    if (!twoFASetupSkipKey) {
      setHas2FASetupBeenOffered(false);
      return;
    }

    setHas2FASetupBeenOffered(sessionStorage.getItem(twoFASetupSkipKey) === 'true');
  }, [twoFASetupSkipKey]);

  // Handle successful 2FA verification
  const handle2FASuccess = () => {
    setIs2FAVerified(true);
    setShow2FAModal(false);
    
    // Persist 2FA verification status for this session
    sessionStorage.setItem('2fa_verified', 'true');
    
    // Log successful 2FA verification
    logAction('login', 'authentication', {
      success: true,
      details: { 
        verification_method: '2fa',
        user_agent: navigator.userAgent,
        user_type: adminStatus === 'APPROVED' ? 'admin' : 'finance'
      }
    });
  };

  // Handle 2FA cancellation (logout)
  const handle2FACancel = () => {
    setShow2FAModal(false);
    handleLogout();
  };

  // Handle 2FA setup completion - the 2FA status will update automatically
  const handle2FASetupComplete = () => {
    setHas2FASetupBeenOffered(true);
    if (twoFASetupSkipKey) {
      sessionStorage.setItem(twoFASetupSkipKey, 'true');
    }
    // The use2FA hook will automatically update is2FAEnabled state
    // This will cause a re-render and the user will proceed to verification or main app
  };

  // Handle skipping 2FA setup - continue to main app without 2FA
  const handle2FASetupSkip = () => {
    setHas2FASetupBeenOffered(true);
    if (twoFASetupSkipKey) {
      sessionStorage.setItem(twoFASetupSkipKey, 'true');
    }
    // User chose to skip 2FA setup, continue to main app
    // The needs2FASetup condition will become false since has2FASetupBeenOffered is now true
  };

  // Listen for logout events
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (performance.navigation?.type === 1) { // Navigation type reload
        setIsLoggingOut(false);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Reset logout state when authentication changes
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      setIsLoggingOut(false);
    }
  }, [isAuthenticated, isLoading]);

  // Log successful login
  useEffect(() => {
    if (isAuthenticated && user && userApproved && !hasLoggedLogin) {
      logAction('login', 'authentication', {
        success: true,
        details: { 
          login_method: 'auth0',
          user_agent: navigator.userAgent,
          user_type: adminStatus === 'APPROVED' ? 'admin' : 'finance'
        }
      });
      setHasLoggedLogin(true);
    }
  }, [isAuthenticated, user, userApproved, hasLoggedLogin, logAction, adminStatus]);

  // Set a timeout for loading state to prevent infinite loading
  useEffect(() => {
    // Don't set timeout if user is logging out or if not authenticated
    if ((isLoading || (overallLoading && isAuthenticated)) && !isLoggingOut) {
      const timer = setTimeout(() => {
        setLoadingTimeout(true);
      }, 3000); // Reduced to 3 seconds

      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [isLoading, overallLoading, isAuthenticated, isLoggingOut]);

  // Clear 2FA verification status when user is no longer authenticated
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      sessionStorage.removeItem('2fa_verified');
      setIs2FAVerified(false);
    }
  }, [isAuthenticated, isLoading]);

  // Provide logout function to child components
  const handleLogout = () => {
    setIsLoggingOut(true);
    setLoadingTimeout(false);
    setHasLoggedLogin(false);
    
    // Clear any stored auth data
    localStorage.removeItem('auth0.is.authenticated');
    localStorage.removeItem('auth0.user');
    sessionStorage.clear();
    
    // Logout with Auth0
    logout({ 
      logoutParams: { 
        returnTo: window.location.origin 
      } 
    });
  };

  // Show loading state with timeout fallback (but not during logout)
  if ((isLoading || (overallLoading && isAuthenticated)) && !loadingTimeout && !isLoggingOut) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Loading...</h2>
          <p className="text-slate-400">Authenticating your session</p>
        </div>
      </div>
    );
  }

  // Show loading timeout fallback if auth/session restoration is taking longer than expected.
  if (loadingTimeout && !isLoggingOut && (isAuthenticated || isLoading)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-yellow-500/20 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <AlertCircle className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Authentication Validation Delay</h2>
          <p className="text-yellow-400 mb-6">This is normal after page refresh. Our authentication server is validating your session. You can keep waiting or retry the session restore without losing your current page.</p>
          <div className="space-y-3">
            <button
              onClick={() => setLoadingTimeout(false)}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
            >
              Keep Waiting
            </button>
            <button
              onClick={() => loginWithRedirect(loginOptions)}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
            >
              Reconnect Session
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show logout loading state
  if (isLoggingOut) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Signing Out...</h2>
          <p className="text-slate-400">Redirecting to login</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-red-500/20 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <AlertCircle className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Authentication Error</h2>
          <p className="text-red-400 mb-6">{error.message}</p>
          <button
            onClick={() => loginWithRedirect(loginOptions)}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated (this should show after logout)
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        {/* Background Elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-10 blur-3xl bg-gradient-to-br from-blue-600 to-purple-600"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full opacity-10 blur-3xl bg-gradient-to-br from-purple-600 to-pink-600"></div>
        </div>

        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 max-w-md w-full text-center relative z-10 shadow-2xl">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <Shield className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-3xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent mb-6">
            LockWise
          </h1>
          
          <button
            onClick={() => loginWithRedirect(loginOptions)}
            className="w-full flex items-center justify-center space-x-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl font-medium group"
          >
            <LogIn className="w-5 h-5 group-hover:scale-110 transition-transform" />
            <span>Sign in with your PayinthiID</span>
          </button>
          
          <p className="text-xs text-slate-500 mt-6">
            Access restricted to ICT staff only • <a href="https://sso.payinthi.com" className="text-blue-400 hover:text-blue-300 underline transition-colors">Return to PayinthiID</a>
          </p>
        </div>
      </div>
    );
  }

  // Check user status for authenticated users
  if (isAuthenticated && user) {
    if (userNotFound) {
      // User needs to register - show role selection
      return (
        <UserRegistrationForm 
          onSubmitAdmin={registerAdmin} 
          onSubmitFinance={registerFinanceUser}
          loading={overallLoading} 
        />
      );
    }

    if (userPending) {
      // User is waiting for approval
      if (admin) {
        return <PendingApproval admin={admin} />;
      } else if (financeUser) {
        // Create a compatible object for finance users
        const financeAsAdmin = {
          id: financeUser.id,
          created_at: financeUser.created_at,
          auth0ID: financeUser.auth0ID,
          email: financeUser.email,
          firstname: financeUser.firstname,
          lastname: financeUser.lastname,
          mobilenumber: financeUser.mobilenumber,
          status: financeUser.status
        } as any;
        return <PendingApproval admin={financeAsAdmin} />;
      }
    }

    if (userDenied) {
      // User has been denied access
      if (admin) {
        return <AccessDenied admin={admin} />;
      } else if (financeUser) {
        // Create a compatible object for finance users
        const financeAsAdmin = {
          id: financeUser.id,
          created_at: financeUser.created_at,
          auth0ID: financeUser.auth0ID,
          email: financeUser.email,
          firstname: financeUser.firstname,
          lastname: financeUser.lastname,
          mobilenumber: financeUser.mobilenumber,
          status: financeUser.status
        } as any;
        return <AccessDenied admin={financeAsAdmin} />;
      }
    }

    if (adminStatus === 'APPROVED') {
      // Check if 2FA setup is needed first (user has no 2FA enabled)
      if (needs2FASetup) {
        return (
          <TwoFactorSetupPage 
            onSetupComplete={handle2FASetupComplete}
            onSkip={handle2FASetupSkip}
          />
        );
      }

      // Check if 2FA verification is needed (user has 2FA enabled but not verified)
      if (needs2FA) {
        return (
          <>
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Security Verification Required</h2>
                <p className="text-slate-400">Two-factor authentication is required to access the system</p>
              </div>
            </div>
            <TwoFactorVerificationModal
              isOpen={show2FAModal}
              onSuccess={handle2FASuccess}
              onCancel={handle2FACancel}
            />
          </>
        );
      }
      
      // Admin user - show main app with logout function
      return <AuthProvider logout={handleLogout}>{children}</AuthProvider>;
    }

    if (financeStatus === 'APPROVED') {
      // Check if 2FA setup is needed first (user has no 2FA enabled)
      if (needs2FASetup) {
        return (
          <TwoFactorSetupPage 
            onSetupComplete={handle2FASetupComplete}
            onSkip={handle2FASetupSkip}
          />
        );
      }

      // Check if 2FA verification is needed (user has 2FA enabled but not verified)
      if (needs2FA) {
        return (
          <>
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Security Verification Required</h2>
                <p className="text-slate-400">Two-factor authentication is required to access the system</p>
              </div>
            </div>
            <TwoFactorVerificationModal
              isOpen={show2FAModal}
              onSuccess={handle2FASuccess}
              onCancel={handle2FACancel}
            />
          </>
        );
      }
      
      // Finance user - show finance dashboard with logout function
      return <AuthProvider logout={handleLogout}><FinanceDashboard /></AuthProvider>;
    }
  }

  // Fallback: show loading if we reach here
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Initializing...</h2>
        <p className="text-slate-400">Setting up your session</p>
      </div>
    </div>
  );
}

// Create a simple provider to pass logout function to children
function AuthProvider({ children, logout }: { children: React.ReactNode; logout: () => void }) {
  return (
    <LogoutContext.Provider value={logout}>
      {children}
    </LogoutContext.Provider>
  );
}

// Create context for logout function
const LogoutContext = React.createContext<(() => void) | undefined>(undefined);

export const useLogout = () => {
  const logout = React.useContext(LogoutContext);
  if (!logout) {
    throw new Error('useLogout must be used within AuthProvider');
  }
  return logout;
};
